/**
 * TableActions Component
 *
 * Row action buttons for tables (edit, delete, view, etc.)
 */

import {
  PencilIcon,
  TrashIcon,
  EyeIcon,
  DocumentDuplicateIcon,
  ArrowDownTrayIcon,
  EllipsisHorizontalIcon,
} from '@heroicons/react/24/outline';
import { cn } from '@medsuite/ui-components';
import { type ComponentType, forwardRef, useEffect, useRef, useState } from 'react';

export interface TableAction {
  label: string;
  icon?: ComponentType<{ className?: string }>;
  onClick: () => void;
  variant?: 'default' | 'danger' | 'success' | 'warning';
  disabled?: boolean;
  hidden?: boolean;
}

export interface TableActionsProps {
  actions?: TableAction[];
  onEdit?: () => void;
  onDelete?: () => void;
  onView?: () => void;
  onDuplicate?: () => void;
  onDownload?: () => void;
  className?: string;
  size?: 'sm' | 'md';
  showLabels?: boolean;
  maxVisible?: number;
}

export const TableActions = forwardRef<HTMLDivElement, TableActionsProps>(
  (
    {
      actions = [],
      onEdit,
      onDelete,
      onView,
      onDuplicate,
      onDownload,
      className,
      size = 'md',
      showLabels = false,
      maxVisible = 3,
    },
    ref
  ) => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const menuRef = useRef<HTMLDivElement>(null);

    // Build default actions from props
    const defaultActions: TableAction[] = [];

    if (onView) {
      defaultActions.push({
        label: 'View',
        icon: EyeIcon,
        onClick: onView,
        variant: 'default',
      });
    }

    if (onEdit) {
      defaultActions.push({
        label: 'Edit',
        icon: PencilIcon,
        onClick: onEdit,
        variant: 'default',
      });
    }

    if (onDuplicate) {
      defaultActions.push({
        label: 'Duplicate',
        icon: DocumentDuplicateIcon,
        onClick: onDuplicate,
        variant: 'default',
      });
    }

    if (onDownload) {
      defaultActions.push({
        label: 'Download',
        icon: ArrowDownTrayIcon,
        onClick: onDownload,
        variant: 'default',
      });
    }

    if (onDelete) {
      defaultActions.push({
        label: 'Delete',
        icon: TrashIcon,
        onClick: onDelete,
        variant: 'danger',
      });
    }

    const allActions = [...defaultActions, ...actions].filter((action) => !action.hidden);
    const visibleActions = allActions.slice(0, maxVisible);
    const menuActions = allActions.slice(maxVisible);

    // Close menu when clicking outside
    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
          setIsMenuOpen(false);
        }
      };

      if (isMenuOpen) {
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
      }

      return undefined;
    }, [isMenuOpen]);

    const getVariantClasses = (variant: TableAction['variant'] = 'default') => {
      const baseClasses =
        'inline-flex items-center justify-center rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

      const sizeClasses = {
        sm: 'h-7 w-7 p-1',
        md: 'h-8 w-8 p-1.5',
      }[size];

      const variantClasses = {
        default: 'text-gray-600 hover:bg-gray-100 hover:text-gray-900',
        danger: 'text-red-600 hover:bg-red-50 hover:text-red-700',
        success: 'text-green-600 hover:bg-green-50 hover:text-green-700',
        warning: 'text-yellow-600 hover:bg-yellow-50 hover:text-yellow-700',
      }[variant];

      return cn(baseClasses, sizeClasses, variantClasses);
    };

    if (allActions.length === 0) {
      return null;
    }

    return (
      <div ref={ref} className={cn('flex items-center space-x-1', className)}>
        {visibleActions.map((action, index) => {
          const Icon = action.icon;
          return (
            <button
              key={index}
              onClick={action.onClick}
              disabled={action.disabled}
              className={getVariantClasses(action.variant)}
              title={action.label}
            >
              {Icon && <Icon className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />}
              {showLabels && <span className="ml-1 text-sm">{action.label}</span>}
            </button>
          );
        })}

        {menuActions.length > 0 && (
          <div ref={menuRef} className="relative">
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={getVariantClasses('default')}
              title="More actions"
            >
              <EllipsisHorizontalIcon className={size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} />
            </button>

            {isMenuOpen && (
              <div className="absolute right-0 z-50 mt-1 w-48 rounded-md border border-gray-200 bg-white shadow-lg">
                <div className="py-1">
                  {menuActions.map((action, index) => {
                    const Icon = action.icon;
                    return (
                      <button
                        key={index}
                        onClick={() => {
                          action.onClick();
                          setIsMenuOpen(false);
                        }}
                        disabled={action.disabled}
                        className={cn(
                          'flex w-full items-center px-4 py-2 text-left text-sm',
                          action.variant === 'danger'
                            ? 'text-red-700 hover:bg-red-50'
                            : 'text-gray-700 hover:bg-gray-100',
                          action.disabled && 'cursor-not-allowed opacity-50'
                        )}
                      >
                        {Icon && <Icon className="mr-3 h-4 w-4" />}
                        <span>{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
);

TableActions.displayName = 'TableActions';

export type { TableAction as TableActionType };
